// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type BaseQueryEnhancer, type FetchArgs } from '@reduxjs/toolkit/query';
import {
  type FileResponse,
  type DocumentMimeType,
} from '@shared/data-access/interfaces/api/commons';

/**
 * Options for configuring the binary query.
 *
 * @remarks
 * The binary query is an extension of the base query that allows downloading binary
 * files. The options provided here will be used to construct the response.
 */
export interface BinaryQueryOptions {
  /** The response type for the binary query. */
  readonly responseType?: 'blob' | 'arraybuffer';

  /**
   * The file name for the binary query.
   *
   * @remarks
   * This can either be a string in the format `filename.ext` or a function that
   * takes the query arguments and returns a string in the same format.
   */
  readonly fileName?:
    | `${string}.${string}`
    | ((args: FetchArgs) => `${string}.${string}`);

  /** The MIME type for the binary query. */
  readonly mimeType?: DocumentMimeType;
}

/**
 * Enhancer for binary queries used in RTK Query.
 *
 * This enhancer allows downloading binary files such as images or documents. It
 * provides options for setting the response type, file name, and MIME type. When
 * the response type is specified, the enhancer constructs a Blob from the response,
 * creates an object URL, and provides this URL along with the file name.
 *
 * @example
 *
 * ```typescript
 * const api = createApi({
 *   baseQuery: binaryQuery(fetchBaseQuery({ baseUrl: '/api' })),
 *   endpoints: builder => ({
 *     downloadFile: builder.query({
 *       query: id => ({
 *         url: `/files/${id}`,
 *         extraOptions: {
 *           responseType: 'blob',
 *           fileName: `file-${id}.pdf`,
 *           mimeType: 'application/pdf',
 *         },
 *       }),
 *     }),
 *   }),
 * });
 * ```
 *
 * @param baseQuery - The base query function that this enhancer extends.
 * @returns A query function that can handle binary file responses.
 */
export const binaryQuery: BaseQueryEnhancer<
  FetchArgs,
  BinaryQueryOptions | void,
  void
> = baseQuery => {
  const getBinaryResponseData = (
    args: FetchArgs,
    blob: BlobPart,
    extraOptions: BinaryQueryOptions,
  ): FileResponse => {
    if (
      extraOptions.responseType &&
      (!extraOptions.fileName || !extraOptions.mimeType)
    ) {
      throw new Error(`Please provide responseType, fileName, and mimeType.`);
    }
    const fileName =
      typeof extraOptions.fileName === 'function'
        ? extraOptions.fileName(args)
        : extraOptions.fileName;

    const file = new File([blob], fileName as string, {
      type: extraOptions.mimeType,
    });

    const url = URL.createObjectURL(file);

    return {
      url,
      filename: fileName as string,
    };
  };

  return async (args, api, extraOptions): Promise<any> => {
    switch (extraOptions?.responseType) {
      case 'blob':
        return baseQuery(
          {
            ...args,
            responseHandler: async (response: Response) => {
              const blob = await response.blob();

              return getBinaryResponseData(args, blob, extraOptions);
            },
          },
          api,
          extraOptions,
        );
      case 'arraybuffer':
        return baseQuery(
          {
            ...args,
            responseHandler: async (response: Response) => {
              const blob = await response.arrayBuffer();

              return getBinaryResponseData(args, blob, extraOptions);
            },
          },
          api,
          extraOptions,
        );

      default:
        return baseQuery(args, api, extraOptions);
    }
  };
};
